/*
 * Helpers del dashboard de Operaciones sin Facturar.
 * Replican la logica y la paleta del HTML original (cleanRow, groupSum, buckets).
 */

//Metadata quemada de la carga original
export const carteraMeta = {
  publishedAt: "15 de agosto de 2026",
  sheetName: "SABANA DO'S",
  recordCount: 6152,
};

//Paleta del HTML original
export const carteraColors = [
  "#122459", "#E8472A", "#3D5AA9", "#F5A623", "#6B7FC4", "#1E9E6B", "#A7B3DC", "#F2916F",
];

//Colores base del HTML original
export const htmlTheme = {
  navy: "#122459",
  navy2: "#1B2F6E",
  navyLight: "#3D5AA9",
  coral: "#E8472A",
  coralDark: "#C63A20",
  amber: "#F5A623",
  green: "#1E9E6B",
  bg: "#EEF1F8",
  card: "#FFFFFF",
  ink: "#1D2333",
  muted: "#6B7280",
  border: "#E3E6EE",
};

//Etiquetas legibles de las claves de agrupacion
export const keyLabels = {
  GERENCIA: "Gerencia",
  NOMBRE: "Cliente",
  "TIPO DE OPERACIÓN": "Tipo de Operación",
  "DIRECTOR DE CUENTA": "Director de Cuenta",
};

/**
 * Formatea un numero como pesos colombianos
 * @param {number} nValue Valor a formatear
 * @returns {string} Valor en formato COP
 */
export const fmtCOP = (nValue) => "$" + Math.round(nValue || 0).toLocaleString("es-CO");

/**
 * Redondea un rango a un paso "bonito" (1, 2 o 5 por potencia de 10)
 * @param {number} nRange Rango a redondear
 * @returns {number} Paso redondeado
 */
const niceNum = (nRange) => {
  const nExp = Math.floor(Math.log10(nRange));
  const nF = nRange / 10 ** nExp;
  const nNf = nF <= 1 ? 1 : nF <= 2 ? 2 : nF <= 5 ? 5 : 10;
  return nNf * 10 ** nExp;
};

/**
 * Genera los ticks del eje de una gráfica (como los automáticos de Chart.js)
 * @param {number} nMax Valor máximo de la serie
 * @param {number} nCount Cantidad aproximada de ticks
 * @returns {Object} { ticks, top } ticks ascendentes y tope de la escala
 */
export const buildTicks = (nMax, nCount = 5) => {
  const nStep = niceNum(Math.max(1, nMax) / nCount);
  const nTop = Math.ceil(Math.max(1, nMax) / nStep) * nStep;
  const mTicks = [];
  for (let nT = 0; nT <= nTop; nT += nStep) mTicks.push(nT);
  return { ticks: mTicks, top: nTop };
};

/**
 * Trunca una etiqueta larga con puntos suspensivos (como el HTML original)
 * @param {string} sText Texto a truncar
 * @param {number} nMax Largo máximo
 * @returns {string} Texto truncado
 */
export const truncate = (sText, nMax) =>
  sText && sText.length > nMax ? sText.substring(0, nMax) + "…" : sText;

/**
 * Indica si un valor es numerico (para detectar columnas NOMBRE/ID intercambiadas)
 * @param {*} vValue Valor a evaluar
 * @returns {boolean}
 */
const isNumeric = (vValue) =>
  vValue !== null && vValue !== undefined && vValue !== "" && !isNaN(Number(vValue));

//Formato ISO UTC que trae el dataset embebido (el resto queda como texto, ej "0000-00-00")
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

/**
 * Convierte a Date las cadenas ISO del dataset quemado y deja pasar los Date
 * que ya entrega xlsx al cargar la sabana. El resto (ej "0000-00-00") es null.
 * @param {*} vValue Valor crudo de la columna de fecha
 * @returns {Date|null} Fecha valida o null si no aplica
 */
const parseIsoDate = (vValue) => {
  if (vValue instanceof Date) return isNaN(vValue) ? null : vValue;
  return typeof vValue === "string" && ISO_DATE_RE.test(vValue) ? new Date(vValue) : null;
};

/**
 * Calcula la diferencia en dias completos entre hoy y una fecha
 * @param {Date} dDate Fecha base
 * @param {Date} dNow Fecha actual
 * @returns {number|null} Dias transcurridos o null si la fecha es invalida
 */
const diffDays = (dDate, dNow) => {
  if (!(dDate instanceof Date) || isNaN(dDate)) return null;
  return Math.floor((dNow - dDate) / (1000 * 60 * 60 * 24));
};

//Nombres canonicos de las columnas que consume cleanRow
const CANONICAL_HEADERS = {
  ID: "ID",
  NOMBRE: "NOMBRE",
  GERENCIA: "GERENCIA",
  DO: "DO",
  "ANO DEL DO": "AÑO DEL DO",
  "TIPO DE OPERACION": "TIPO DE OPERACIÓN",
  "FECHA DE CREACION": "FECHA DE CREACIÓN",
  "FECHA DE LEVANTE": "FECHA DE LEVANTE",
  VALOR: "VALOR",
  "ID DIRECTOR DE CUENTA": "ID DIRECTOR DE CUENTA",
  "DIRECTOR DE CUENTA": "DIRECTOR DE CUENTA",
};

/**
 * Quita las tildes de una cadena
 * @param {string} sText Texto a limpiar
 * @returns {string} Texto sin tildes
 */
const stripAccents = (sText) => sText.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

/**
 * Lleva los encabezados de una fila a los nombres canonicos. El archivo mensual
 * a veces trae espacios de mas o sin tilde (ej "FECHA DE CREACION " en vez de
 * "FECHA DE CREACIÓN"), asi el dataset se pega tal cual sale del HTML.
 * @param {Object} mRow Fila cruda del dataset
 * @returns {Object} Fila con encabezados canonicos
 */
const normalizeRowHeaders = (mRow) => {
  const mOut = {};
  Object.keys(mRow).forEach((sKey) => {
    const sNorm = stripAccents(sKey.trim().toUpperCase());
    mOut[CANONICAL_HEADERS[sNorm] || sKey] = mRow[sKey];
  });
  return mOut;
};

/**
 * Normaliza una fila cruda del dataset al formato que consume el dashboard
 * @param {Object} mRow Fila cruda del dataset
 * @param {Date} dNow Fecha actual para calcular antigüedad
 * @returns {Object} Fila normalizada con campos derivados
 */
const cleanRow = (mRow, dNow) => {
  const sGerencia = (mRow["GERENCIA"] || "").toString().trim().toUpperCase();
  let sTipo = mRow["TIPO DE OPERACIÓN"];
  if (typeof sTipo === "string") sTipo = sTipo.trim();
  if (sTipo === 0 || sTipo === "#N/A" || sTipo === null || sTipo === undefined || sTipo === "") {
    sTipo = "SIN CLASIFICAR";
  }
  //El archivo trae el mismo director con y sin espacio al final: sin trim sale dos veces en la grafica
  let sDirector = mRow["DIRECTOR DE CUENTA"];
  if (typeof sDirector === "string") sDirector = sDirector.trim();
  if (!sDirector || sDirector === "NO REGISTRA" || sDirector === "NULL") sDirector = "Sin asignar";

  let vNombre = mRow["NOMBRE"];
  let vId = mRow["ID"];
  if (isNumeric(vNombre) && !isNumeric(vId) && vId) {
    const vTmp = vNombre;
    vNombre = vId;
    vId = vTmp;
  }

  const dCreacion = parseIsoDate(mRow["FECHA DE CREACIÓN"]);
  const dLevanteRaw = parseIsoDate(mRow["FECHA DE LEVANTE"]);
  const bTieneLevante = dLevanteRaw instanceof Date && !isNaN(dLevanteRaw);
  const fmtFecha = (dDate) =>
    dDate instanceof Date && !isNaN(dDate) ? dDate.toLocaleDateString("es-CO") : null;

  return {
    NOMBRE: vNombre || "Sin nombre",
    ID: vId,
    GERENCIA: sGerencia || "SIN GERENCIA",
    DO: mRow["DO"],
    ANIO: mRow["AÑO DEL DO"],
    "TIPO DE OPERACIÓN": sTipo,
    "DIRECTOR DE CUENTA": sDirector,
    VALOR: Number(mRow["VALOR"]) || 0,
    _dias: diffDays(dCreacion, dNow),
    _tieneLevante: bTieneLevante,
    _diasLevante: bTieneLevante ? diffDays(dLevanteRaw, dNow) : null,
    _fechaCreacionStr: fmtFecha(dCreacion) || "Sin fecha",
    _fechaLevanteStr: bTieneLevante ? fmtFecha(dLevanteRaw) : "Sin levante",
  };
};

/**
 * Normaliza todo el dataset una sola vez
 * @param {Array} mDataset Dataset crudo
 * @returns {Array} Filas normalizadas con DO válido
 */
export const prepareRows = (mDataset) => {
  const dNow = new Date();
  return mDataset
    .map((mRow) => cleanRow(normalizeRowHeaders(mRow), dNow))
    .filter((mRow) => mRow.DO);
};

/**
 * Suma el VALOR agrupando por una clave
 * @param {Array} mData Filas a agrupar
 * @param {string} sKey Clave de agrupacion
 * @returns {Object} Mapa clave → suma
 */
export const groupSum = (mData, sKey) => {
  const mMap = {};
  mData.forEach((mRow) => {
    const sK = mRow[sKey] ?? "N/A";
    mMap[sK] = (mMap[sK] || 0) + mRow.VALOR;
  });
  return mMap;
};

//Clasificaciones de semáforo por rango de días
export const bucketAgingGeneral = (nDias) => (nDias <= 30 ? "verde" : nDias <= 60 ? "amarillo" : "rojo");
export const bucketSinLevante = (nDias) => (nDias <= 7 ? "verde" : nDias <= 20 ? "amarillo" : "rojo");
export const bucketConLevante = (nDias) => (nDias <= 3 ? "verde" : nDias <= 7 ? "amarillo" : "rojo");

//Colores hex de cada rango de semáforo (los del HTML)
export const bucketColors = { verde: "#1E9E6B", amarillo: "#F5A623", rojo: "#E8472A" };

//Gradientes de las tarjetas semáforo (los del HTML)
export const bucketGradients = {
  verde: "linear-gradient(135deg,#1E9E6B,#17805A)",
  amarillo: "linear-gradient(135deg,#F5A623,#D6890C)",
  rojo: "linear-gradient(135deg,#E8472A,#B8341E)",
};

//Etiquetas de cada rango de semáforo (las del HTML)
export const bucketLabels = { verde: "🟢 En rango", amarillo: "🟡 Atención", rojo: "🔴 Crítico" };
